<template>
  <div class="" v-loader.opacity.tab="loading">
    <div class="title-box">
      <h2 class="title">{{ form.uuid ? $t('schedule.edit') : $t('schedule.new') }}</h2>
    </div>

    <div class="alert" :class="alert.class" role="alert" v-if="alert.message">
      <span v-html="alert.message"></span>
      <button type="button" class="btn-close" aria-label="Close" @click="alert.message = null"></button>
    </div>

    <div class="clinic-schedule-box scrollbar">
      <section>
        <div class="row">
          <div class="col-lg-6">
            <div class="form-floating">
              <input type="text" class="form-control" ref="name" id="name" v-model="form.name" :placeholder="$t('schedule.name')" data-test="inputScheduleName">
              <label for="name">{{ $t('schedule.name') }}*</label>
            </div>
          </div>
          <div class="col-lg-3">
            <div class="form-floating v-select-floating">
              <v-select
              class="v-select form"
              id="typeSelected"
              :placeholder="$t('schedule.type')"
              v-model="form.type"
              data-test="vSelectScheduleType"
              :reduce="x => x.value"
              :clearable="false"
              :options="availableTypeOptions">
            </v-select>
            <label for="typeSelected">{{ $t('schedule.type') }}</label>
          </div>
        </div>
        <div class="col-lg-3">
          <div class="form-floating">
            <div class="form-check form-switch">
              <label class="form-check-label" for="scheduleEnabled">{{ $t('schedule.enabled') }}</label>
              <input class="form-check-input"
                type="checkbox"
                role="switch"
                id="scheduleEnabled"
                v-model="form.enabled"
                data-test="checkScheduleEnabled">
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="form.type === 'report'">
        <div class="row">
          <div class="col-lg-12">
            <div class="form-check form-switch">
              <label class="form-check-label" for="scheduleNotSynced">{{ $t('schedule.notSynced') }}</label>
              <input class="form-check-input"
                type="checkbox"
                role="switch"
                id="scheduleNotSynced"
                v-model="form.notSynced"
                data-test="checkScheduleNotSynced">
            </div>
            <p class="schedule-hint">{{ $t('schedule.notSyncedHint') }}</p>
          </div>
        </div>
      </section>

      <section>
        <div class="row">
          <div class="col-lg-6" v-if="!form.notSynced">
            <div class="form-floating v-select-floating">
              <v-select
                class="v-select form"
                id="dateRangeSelected"
                :placeholder="$t('schedule.dateRange')"
                v-model="form.dateRange"
                data-test="vSelectScheduleDateRange"
                :reduce="x => x.value"
                :clearable="false"
                :options="dateRangeOptions">
                </v-select>
              <label for="dateRangeSelected">{{ $t('schedule.dateRange') }}</label>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="form-floating v-select-floating">
              <v-select
                class="v-select form"
                id="integrationSelected"
                :placeholder="$t('schedule.integration')"
                v-model="form.integration"
                data-test="vSelectScheduleIntegration"
                :reduce="x => x.value"
                :options="integrationOptions">
              </v-select>
              <label for="integrationSelected">{{ $t('schedule.integration') }}</label>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h3 class="sub-title">{{ $t('schedule.schedule') }}</h3>

        <div class="row">
          <div class="col-lg-4">
            <div class="form-floating v-select-floating">
              <v-select
                class="v-select form"
                id="frequencySelected"
                :placeholder="$t('schedule.frequency')"
                v-model="form.frequency"
                data-test="vSelectScheduleFrequency"
                :reduce="x => x.value"
                :clearable="false"
                :options="frequencyOptions">
              </v-select>
              <label for="frequencySelected">{{ $t('schedule.frequency') }}</label>
            </div>
          </div>

          <div class="col-lg-4" v-if="form.frequency === 'weekly'">
            <div class="form-floating v-select-floating">
              <v-select
                class="v-select form"
                id="weekdaySelected"
                :placeholder="$t('schedule.weekday')"
                v-model="form.weekday"
                data-test="vSelectScheduleWeekday"
                :reduce="x => x.value"
                :clearable="false"
                :options="weekdayOptions">
              </v-select>
              <label for="weekdaySelected">{{ $t('schedule.weekday') }}</label>
            </div>
          </div>

          <div class="col-lg-4" v-if="form.frequency === 'monthly'">
            <div class="form-floating v-select-floating">
              <v-select
                class="v-select form"
                id="dayOfMonthSelected"
                :placeholder="$t('schedule.dayOfMonth')"
                v-model="form.dayOfMonth"
                data-test="vSelectScheduleDayOfMonth"
                :reduce="x => x.value"
                :clearable="false"
                :options="dayOfMonthOptions">
              </v-select>
              <label for="dayOfMonthSelected">{{ $t('schedule.dayOfMonth') }}</label>
            </div>
          </div>

          <div class="col-lg-4" v-if="form.frequency === 'custom'">
            <div class="form-floating v-select-floating">
              <v-select
                multiple
                class="v-select form grown"
                id="weekdaysSelected"
                :placeholder="$t('schedule.weekdays')"
                v-model="form.weekdays"
                data-test="vSelectScheduleWeekdays"
                :reduce="x => x.value"
                :options="weekdayOptions">
              </v-select>
              <label for="weekdaysSelected">{{ $t('schedule.weekdays') }}</label>
            </div>
          </div>

          <div class="col-lg-4">
            <div class="form-floating v-select-floating">
              <v-select
                class="v-select form"
                id="hourSelected"
                :placeholder="$t('schedule.hour')"
                v-model="form.hour"
                data-test="vSelectScheduleHour"
                :reduce="x => x.value"
                :clearable="false"
                :options="hourOptions">
              </v-select>
              <label for="hourSelected">{{ $t('schedule.hour') }}</label>
            </div>
          </div>
        </div>
      </section>

      <p class="schedule-hint">
        {{ $t('schedule.hourHint') }}
        <br/>
        {{ $t('schedule.currentServerTime') }}: {{ serverTime }}
      </p>
    </div>

    <div class="action-bar">
      <button class="btn btn-success" type="button" name="button" data-test="btnSave" @click="doSave">
        <span>{{ $t('save') }}</span>
      </button>
      <button class="btn btn-secondary" type="button" name="button" data-test="btnCancel" @click="$emit('cancel-edit')">
        <span>{{ $t('cancel') }}</span>
      </button>
      <button v-if="form.uuid" class="btn btn-outline-danger" type="button" name="button" data-test="btnRemove" @click="confirmRemoval">
        <i class="fa fa-ban"></i>
        <span>{{ $t('remove') }}</span>
      </button>
    </div>

    <BaseGenericOffcanvas :offcanvas_id="'offcanvasClinicScheduleGeneric'" ref="offcanvasGeneric"></BaseGenericOffcanvas>
  </div>
</template>

<style lang="scss" scoped>
.clinic-schedule-box {
  height:                       calc(100dvh - 394px);
  padding:                      10px;
}

.schedule-hint {
  color:                        var(--Shades-70);
}

:deep(.v-select.grown .vs__selected) {
  background-color:             var(--Brand-Primary-BG);
  border-color:                 var(--Brand-Primary-BG);
  color:                        #FFF !important;
}

:deep(.v-select.grown .vs__deselect) {
  fill:                         #FFF;
}

section {
  display:                      flex;
  flex-direction:               column;
  gap:                          19px;
  margin-bottom:                19px;
}

@media (max-width: 991px) {
  .title-box {
    margin-bottom:              0;
  }

  .clinic-schedule-box {
    height:                     calc(100dvh - 309px);
    overflow-x:                 hidden;
    overflow-y:                 scroll;
  }

  section {
    .row {
      .col-lg-3,
      .col-lg-6:last-child {
        margin-top:             19px;
      }
    }

    .row .col-lg-4:not(:last-child) {
      margin-bottom:            19px;
    }
  }
}

@media screen and (min-width: 992px) and (max-width: 1365px) {
  .clinic-schedule-box {
    height:                     calc(100dvh - 358px);
    overflow-x:                 hidden;
    overflow-y:                 scroll;
  }
}
</style>

<script>
function buildForm(source) {
  if (source && source.uuid) {
    return {
      uuid: source.uuid,
      type: source.type || 'report',
      name: source.name,
      enabled: source.enabled !== undefined ? source.enabled : true,
      integration: source.integration || null,
      frequency: source.frequency || 'daily',
      hour: source.hour !== undefined && source.hour !== null ? source.hour : 8,
      weekday: source.weekday !== undefined && source.weekday !== null ? source.weekday : 1,
      dayOfMonth: source.dayOfMonth !== undefined && source.dayOfMonth !== null ? source.dayOfMonth : 1,
      weekdays: Array.isArray(source.weekdays) ? source.weekdays.slice() : [],
      dateRange: source.dateRange || 'previousDay',
      notSynced: !!source.notSynced,
    }
  }
  return {
    uuid: null,
    type: 'report',
    name: null,
    enabled: true,
    integration: null,
    frequency: 'daily',
    hour: 8,
    weekday: 1,
    dayOfMonth: 1,
    weekdays: [],
    dateRange: 'previousDay',
    notSynced: false,
  }
}

export default {
  name: 'ClinicScheduleEdit',
  props: ['clinic_id', 'schedule'],
  data: () => ({
    loading: false,
    alert: {
      message: null,
      class: null
    },
    typeOptions: [],
    integrationOptions: [],
    frequencyOptions: [],
    hourOptions: [],
    weekdayOptions: [],
    dayOfMonthOptions: [],
    dateRangeOptions: [],
    form: buildForm(null),
    serverTime: null,
    serverTimeTimer: null,
  }),
  computed: {
    availableTypeOptions() {
      if (!this.form.notSynced) {
        return this.typeOptions
      }
      return this.typeOptions.filter(option => option.value === 'report')
    }
  },
  watch: {
    schedule: {
      immediate: true,
      handler(value) {
        this.form = buildForm(value)
      }
    },
    'form.type'(value) {
      if (value !== 'report') {
        this.form.notSynced = false
      }
    }
  },
  mounted() {
    this.typeOptions = [
      {value: 'report', label: this.$i18n.t('schedule.typeReport')},
      {value: 'claim', label: this.$i18n.t('schedule.typeClaim')},
    ]

    this.frequencyOptions = [
      {value: 'daily', label: this.$i18n.t('schedule.freqDaily')},
      {value: 'weekly', label: this.$i18n.t('schedule.freqWeekly')},
      {value: 'monthly', label: this.$i18n.t('schedule.freqMonthly')},
      {value: 'custom', label: this.$i18n.t('schedule.freqCustom')},
    ]

    this.dateRangeOptions = [
      {value: 'previousDay', label: this.$i18n.t('schedule.rangePreviousDay')},
      {value: 'previousWeek', label: this.$i18n.t('schedule.rangePreviousWeek')},
      {value: 'previousMonth', label: this.$i18n.t('schedule.rangePreviousMonth')},
      {value: 'last7Days', label: this.$i18n.t('schedule.rangeLast7Days')},
      {value: 'last30Days', label: this.$i18n.t('schedule.rangeLast30Days')},
    ]

    this.weekdayOptions = [
      {value: 0, label: this.$i18n.t('schedule.weekdaySun')},
      {value: 1, label: this.$i18n.t('schedule.weekdayMon')},
      {value: 2, label: this.$i18n.t('schedule.weekdayTue')},
      {value: 3, label: this.$i18n.t('schedule.weekdayWed')},
      {value: 4, label: this.$i18n.t('schedule.weekdayThu')},
      {value: 5, label: this.$i18n.t('schedule.weekdayFri')},
      {value: 6, label: this.$i18n.t('schedule.weekdaySat')},
    ]

    this.hourOptions = []
    for (let h = 0; h < 24; h++) {
      this.hourOptions.push({value: h, label: (h < 10 ? '0' + h : '' + h) + ':00'})
    }

    this.dayOfMonthOptions = []
    for (let d = 1; d <= 28; d++) {
      this.dayOfMonthOptions.push({value: d, label: '' + d})
    }

    this.fetchIntegrations()

    this.updateServerTime()
    this.serverTimeTimer = setInterval(this.updateServerTime, 30000)

    this.$nextTick(() => {
      if (this.$refs.name) this.$refs.name.focus()
    })
  },
  beforeUnmount() {
    if (this.serverTimeTimer) {
      clearInterval(this.serverTimeTimer)
      this.serverTimeTimer = null
    }
  },
  methods: {
    updateServerTime() {
      this.serverTime = moment.utc().format('hh:mm A')
    },
    fetchIntegrations: async function() {
      try {
        let response = await this.$clinicService.listIntegrations({clinicId: this.clinic_id})
        let items = response.result ? response.result : []
        this.integrationOptions = items
          .filter(i => i.type === 'sftp')
          .map(i => ({value: i.uuid, label: i.name + (i.enabled ? '' : ' (' + this.$i18n.t('schedule.statusDisabled') + ')')}))
      } catch (err) {
        console.log(err)
      }
    },
    doSave: async function() {
      this.alert.message = null

      if (!this.form.name) {
        this.$message.popup(this, 'schedule.nameRequired', 'operationWarning', 'warn')
        return
      }
      if (this.form.frequency === 'custom' && (!this.form.weekdays || !this.form.weekdays.length)) {
        this.$message.popup(this, 'schedule.weekdaysRequired', 'operationWarning', 'warn')
        return
      }

      let params = {
        clinicId: this.clinic_id,
        schedule: this.form
      }

      this.loading = true
      NProgress.start()
      try {
        await this.$clinicService.saveSchedule(params)
        this.$message.popup(this, 'general.createdSuccess', 'operationSuccess', 'success')
        this.$emit('saved')
      } catch (err) {
        console.log(err)
        this.$message.popup(this, 'general.createdError', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
    async confirmRemoval() {
      const ok = await this.$refs.offcanvasGeneric.show({
        title: this.$i18n.t('confirm'),
        text: this.$i18n.t('general.removeConfirm'),
        okButton: this.$i18n.t('remove'),
        okButtonClass: 'btn-danger',
      })
      if (ok) {
        this.doRemove()
      }
    },
    doRemove: async function() {
      this.loading = true
      NProgress.start()
      try {
        await this.$clinicService.removeSchedule({clinicId: this.clinic_id, uuid: this.form.uuid})
        this.$message.popup(this, 'schedule.removeSuccess', 'operationSuccess', 'success')
        this.$emit('saved')
      } catch (err) {
        console.log(err)
        this.$message.popup(this, 'schedule.removeError', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
  },
}
</script>
